import {
  type ClipboardEvent,
  type KeyboardEvent,
  type MouseEvent,
  useLayoutEffect,
  useRef,
} from 'react';
import { klasse } from '../ui/klasse.js';
import type { Block as BlockDaten } from './bloecke.js';
import { signatur, uebersetzeBlock } from './rueckuebersetzung.js';
import stile from './text-flaeche.module.css';
import website from './website-text.module.css';

export interface BlockProps {
  block: BlockDaten;
  /** Kachel im Karussell (`istKachel`); die Komponente kennt die Bausteine selbst nicht. */
  kachel: boolean;
  /** Wiederholt nur den Bausteinnamen (`istDoppelteBeschriftung`) und wird ausgeblendet. */
  dopplung?: boolean;
  html: { __html: string };
  abschnittId: string | undefined;
  hinweisId: string;
  /** Darf die Seite gerade bearbeiten (nicht nach dem Absenden, nicht waehrend des Verwerfens)? */
  bearbeitbar: boolean;
  offen: boolean;
  onKlick: (blockIndex: number) => void;
  onOeffnen: (blockIndex: number) => void;
  onEingabe: (blockIndex: number, raw: string) => void;
  onSchliessen: (blockIndex: number, raw: string) => void;
  onAbbrechen: (blockIndex: number) => void;
}

/**
 * Der Klick im Lesezustand hat bereits eine leere Auswahl an die Klickstelle gesetzt; liegt
 * sie im Block, ist das genau der gewuenschte Cursor. Sonst (Enter per Tastatur) ans Ende.
 * Der Browsertest (Task 17) prueft das in Chrome; faellt er durch, hier
 * `caretPositionFromPoint` mit den Klickkoordinaten nachruesten.
 */
function setzeCaret(element: HTMLElement): void {
  const auswahl = window.getSelection();
  if (!auswahl) return;
  if (auswahl.rangeCount > 0 && auswahl.isCollapsed && element.contains(auswahl.anchorNode)) return;
  const bereich = document.createRange();
  bereich.selectNodeContents(element);
  bereich.collapse(false);
  auswahl.removeAllRanges();
  auswahl.addRange(bereich);
}

/**
 * Codex-Befund (HOCH): Ein eingebetteter Link (aus `als-html.ts`) soll sich anklicken und per
 * Enter oeffnen lassen, ohne dass der Block das Ereignis fuer sich selbst beansprucht. `closest`
 * findet den Link auch, wenn das Ereignisziel ein Nachfahre ist (z. B. fett gesetzter Linktext).
 */
function istZielEinLink(ziel: EventTarget | null): boolean {
  return ziel instanceof Element && ziel.closest('a') !== null;
}

function fuegeKlartextEin(text: string): void {
  // `insertText` erhaelt das Undo des Browsers; wo es fehlt (jsdom), von Hand einfuegen.
  if (
    typeof document.execCommand === 'function' &&
    document.execCommand('insertText', false, text)
  ) {
    return;
  }
  const auswahl = window.getSelection();
  if (!auswahl || auswahl.rangeCount === 0) return;
  const bereich = auswahl.getRangeAt(0);
  bereich.deleteContents();
  const knoten = document.createTextNode(text);
  bereich.insertNode(knoten);
  bereich.setStartAfter(knoten);
  bereich.collapse(true);
  auswahl.removeAllRanges();
  auswahl.addRange(bereich);
}

export function Block({
  block,
  kachel,
  dopplung = false,
  html,
  abschnittId,
  hinweisId,
  bearbeitbar,
  offen,
  onKlick,
  onOeffnen,
  onEingabe,
  onSchliessen,
  onAbbrechen,
}: BlockProps) {
  const element = useRef<HTMLDivElement>(null);
  const signaturBeimOeffnen = useRef('');
  // Nach Escape folgt ein blur -- der darf nicht zusaetzlich als "Schliessen" gelten.
  const erledigt = useRef(false);

  // H1 (Codex-Runde 2): Ein offener Block ist nur editierbar, solange die Seite bearbeiten darf.
  // Waehrend Absenden oder Verwerfen laufen und nach `runde_veraltet` gingen weitere Eingaben
  // sonst verloren: `absenden` hat den Stand beim Klick schon erfasst und setzt ihn nach Erfolg.
  // Der Inhalt bleibt stehen. Wird die Seite wieder frei (Absenden gescheitert, Ruling 23), ist
  // der Block wieder editierbar und bekommt den Fokus zurueck -- sein spaeterer Blur schliesst
  // ihn wie gewohnt. Vor dem Oeffnen-Effekt deklariert, damit der Fokus einen editierbaren
  // Block trifft.
  const warGesperrt = useRef(false);
  useLayoutEffect(() => {
    const el = element.current;
    if (!el) return;
    if (!offen) {
      warGesperrt.current = false;
      el.removeAttribute('contenteditable');
      return;
    }
    if (!bearbeitbar) {
      warGesperrt.current = true;
      el.removeAttribute('contenteditable');
      return;
    }
    el.setAttribute('contenteditable', 'true');
    if (warGesperrt.current) {
      warGesperrt.current = false;
      el.focus();
    }
  }, [offen, bearbeitbar]);

  // useLayoutEffect: Signatur und Fokus VOR dem ersten Malen, sonst koennte eine schnelle
  // Eingabe in den noch nicht erfassten Stand fallen. Nur beim Oeffnen, nicht nach einer Sperre:
  // Die Signatur bleibt die vom Oeffnen, sonst zaehlte die Eingabe davor nicht als Aenderung.
  useLayoutEffect(() => {
    const el = element.current;
    if (!el || !offen) return;
    signaturBeimOeffnen.current = signatur(el);
    erledigt.current = false;
    if (typeof document.execCommand === 'function') {
      // Chrome trennt Absaetze sonst mit <div>; die Rueckuebersetzung kennt beides.
      document.execCommand('defaultParagraphSeparator', false, 'p');
    }
    el.focus();
    setzeCaret(el);
  }, [offen]);

  const aktuellerRohtext = () =>
    element.current
      ? uebersetzeBlock(element.current, block, signaturBeimOeffnen.current)
      : block.raw;

  function tasteGedrueckt(ereignis: KeyboardEvent<HTMLDivElement>) {
    // Gesperrt (H1): kein Escape, das die Bearbeitung verwuerfe, kein Tastenkuerzel.
    if (offen && !bearbeitbar) return;
    if (!offen) {
      // Enter auf einem Link soll ihn oeffnen (Standardverhalten des Browsers) statt den Block --
      // deshalb hier kein `preventDefault()` und kein `onOeffnen`.
      if (istZielEinLink(ereignis.target)) return;
      if (ereignis.key === 'Enter' && block.editierbar) {
        ereignis.preventDefault();
        onOeffnen(block.index);
      }
      return;
    }
    if (ereignis.key === 'Escape') {
      ereignis.preventDefault();
      erledigt.current = true;
      // React setzt `innerHTML` nur bei neuem `html`-Objekt -- nach Escape von Hand zurueck.
      if (element.current) element.current.innerHTML = html.__html;
      onAbbrechen(block.index);
      return;
    }
    // Nur fett und kursiv sind erlaubt; Unterstreichen ginge beim Zurueckuebersetzen verloren.
    if ((ereignis.ctrlKey || ereignis.metaKey) && ereignis.key.toLowerCase() === 'u') {
      ereignis.preventDefault();
    }
  }

  function eingefuegt(ereignis: ClipboardEvent<HTMLDivElement>) {
    if (!offen) return;
    ereignis.preventDefault();
    if (!bearbeitbar) return;
    fuegeKlartextEin(ereignis.clipboardData.getData('text/plain'));
    onEingabe(block.index, aktuellerRohtext());
  }

  // M1: Tab-Stopp, Bearbeiten-Hinweis fuer Screenreader und Bearbeiten-Optik (Rahmen beim
  // Ueberfahren, Textcursor) nur, wo ein Klick oder Enter tatsaechlich etwas oeffnet. Sonst
  // versprach der Hinweis nach dem Absenden Bearbeiten, und Tabelle oder Code waren Tab-Stopps
  // ohne Funktion.
  // Eine ausgeblendete Dopplung (`data-dopplung`) ist nie sichtbar und damit auch kein Ziel fuer
  // Tab oder Klick; sonst liefe der Tab-Weg der Leiste am Block (`block-pille.tsx`) ins Leere.
  const oeffenbar = bearbeitbar && block.editierbar && !dopplung;
  return (
    <div
      ref={element}
      className={klasse(website.block, stile.block, oeffenbar && stile.bearbeitbar)}
      data-block-index={block.index}
      data-art={block.art}
      // Kachelregel wie n8n (`bloecke.ts`, `istKachel`): Nur ein Block im Karussell, der mit
      // einem fetten Titel beginnt, ist eine Kachel.
      {...(kachel ? { 'data-kachel': 'true' } : {})}
      {...(dopplung ? { 'data-dopplung': 'true' } : {})}
      {...(abschnittId ? { 'data-abschnitt-id': abschnittId } : {})}
      tabIndex={oeffenbar || offen ? 0 : undefined}
      aria-describedby={oeffenbar && !offen ? hinweisId : undefined}
      onClick={(ereignis: MouseEvent<HTMLDivElement>) => {
        // Ein Klick auf einen eingebetteten Link soll nur den Link oeffnen, nicht zusaetzlich
        // den Editor (Codex-Befund HOCH).
        if (!offen && !istZielEinLink(ereignis.target)) onKlick(block.index);
      }}
      onKeyDown={tasteGedrueckt}
      onInput={() => {
        if (offen && bearbeitbar) onEingabe(block.index, aktuellerRohtext());
      }}
      onPaste={eingefuegt}
      onDragOver={(e) => {
        if (offen) e.preventDefault();
      }}
      onDrop={(e) => {
        if (offen) e.preventDefault();
      }}
      onBlur={() => {
        // Gesperrt (H1) schliesst ein Blur nichts: Das Absenden traegt den Stand schon, und ein
        // Schliessen waehrend des Verwerfens legte den verworfenen Entwurf wieder an.
        if (!offen || erledigt.current || !bearbeitbar) return;
        erledigt.current = true;
        onSchliessen(block.index, aktuellerRohtext());
      }}
      // Bereinigt in `alsHtml` (Allowlist) -- kein react/no-danger-Lint in diesem Projekt aktiv
      dangerouslySetInnerHTML={html}
    />
  );
}
